import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react-lite'
import { mojilandStore } from '../stores/mojilandStore'
import { gamificationStore } from '../stores/gamificationStore'
import { openWaterCourse } from '../courses/openWater'
import type { CourseModule, CourseObjective } from '../courses/openWater'
import { Button } from '../components/ui/Button'

const gameRoutes: Record<string, string> = {
  memoji: '/memoji',
  findmoji: '/findmoji',
  hitmoji: '/hitmoji',
  evoji: '/evoji',
  quizmoji: '/quizmoji',
  timeline: '/timeline',
}

const TerritoryNode = observer(({
  module,
  progress,
  isSelected,
  onSelect,
  index,
}: {
  module: CourseModule
  progress: { completed: number; total: number; unlocked: boolean }
  isSelected: boolean
  onSelect: () => void
  index: number
}) => {
  const completed = progress.completed >= progress.total
  const unlocked = progress.unlocked

  return (
    <button
      onClick={onSelect}
      className={`
        absolute transform -translate-x-1/2 -translate-y-1/2
        w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex flex-col items-center justify-center
        transition-all duration-300 border-2 shadow-xl
        hover:scale-110 active:scale-95
        ${completed
          ? 'bg-gradient-to-br from-emerald-400 to-emerald-600 border-emerald-700 shadow-emerald-500/50 animate-pulse'
          : unlocked
            ? 'bg-gradient-to-br from-cyan-400 to-blue-500 border-cyan-600 shadow-cyan-500/40 hover:shadow-2xl'
            : 'bg-gradient-to-br from-slate-300 to-slate-400 border-slate-500 opacity-60 cursor-not-allowed'
        }
      `}
      style={{
        left: `${module.position.x}%`,
        top: `${module.position.y}%`,
        animationDelay: `${index * 0.1}s`,
      }}
    >
      <span className="text-2xl sm:text-3xl drop-shadow-md">{module.emoji}</span>
      <span className="text-[9px] sm:text-[10px] font-black text-white drop-shadow-sm mt-0.5">
        {progress.completed}/{progress.total}
      </span>
      {!unlocked && (
        <span className="absolute -top-0.5 -right-0.5 text-sm drop-shadow">🔒</span>
      )}
      {completed && (
        <span className="absolute -bottom-0.5 text-lg">✓</span>
      )}
    </button>
  )
})

const ObjectiveItem = ({
  objective,
  completed,
  step,
  isRecommended,
}: {
  objective: CourseObjective
  completed: boolean
  step?: number
  isRecommended?: boolean
}) => {
  const { t } = useTranslation()
  const isQuiz = objective.type === 'quiz'
  const isGame = objective.type === 'game'
  const route = isGame && objective.gameId ? gameRoutes[objective.gameId] : null

  return (
    <div
      className={`
        flex items-center gap-3 p-4 rounded-2xl border-2 transition-all duration-300
        ${completed
          ? 'bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-300 shadow-lg'
          : isRecommended
            ? 'bg-cyan-50/90 border-cyan-400 ring-2 ring-cyan-200 shadow-lg'
            : 'bg-white/95 border-cyan-200 hover:border-cyan-400 hover:shadow-xl hover:scale-[1.02]'
        }
      `}
    >
      {step && (
        <span className="w-8 h-8 rounded-full bg-cyan-200 text-cyan-800 font-black text-sm flex items-center justify-center shrink-0">
          {step}
        </span>
      )}
      <span className="text-3xl">{objective.emoji}</span>
      <div className="flex-1 min-w-0">
        <p className="font-extrabold text-gray-900 truncate">{objective.title}</p>
        <p className="text-xs text-gray-600 truncate">{objective.description}</p>
      </div>
      {completed ? (
        <span className="text-3xl animate-bounce">✅</span>
      ) : isQuiz ? (
        <Button as="link" to={`/quizmoji?module=${objective.quizModuleId}`} variant="primarySolid" size="sm">
          {t('common.playArrow')}
        </Button>
      ) : route ? (
        <Button as="link" to={`${route}?objective=${objective.id}`} variant="primarySolid" size="sm">
          {t('common.playArrow')}
        </Button>
      ) : (
        <span className="text-gray-400 text-sm">{t('mojiland.soon')}</span>
      )}
    </div>
  )
}

const Mojiland = observer(() => {
  const { t } = useTranslation()
  const [selectedModule, setSelectedModule] = useState<CourseModule | null>(null)
  const progress = mojilandStore.getOpenWaterProgress()
  const courseProgress = mojilandStore.getCourseProgressRatio('open-water')
  const totalObjectives = openWaterCourse.modules.reduce((acc, m) => acc + m.objectives.length, 0)
  const completedObjectives = progress.modules.reduce(
    (acc, m) => acc + m.objectivesCompleted.filter(o => o.completed).length,
    0
  )

  // Auto-seleccionar el primer módulo desbloqueado
  useEffect(() => {
    if (!selectedModule) {
      const firstUnlocked = openWaterCourse.modules.find(m => {
        const modProg = progress.modules.find(p => p.moduleId === m.id)
        return modProg?.unlocked ?? false
      })
      if (firstUnlocked) setSelectedModule(firstUnlocked)
    }
  }, [selectedModule, progress.modules])

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-b from-sky-400 via-cyan-300 to-blue-400">
      {/* Olas animadas */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_0%,rgba(255,255,255,0.4)_0%,transparent_50%)]" />
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-blue-600/30 to-transparent" />
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white/20 bubble-float"
            style={{
              width: `${6 + (i % 4) * 6}px`,
              height: `${6 + (i % 4) * 6}px`,
              left: `${(i * 8) % 100}%`,
              top: `${20 + (i * 7) % 60}%`,
              animationDelay: `${i * 0.2}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-4 py-6 pb-28">
        {/* Hero */}
        <div className="text-center mb-4">
          <h1 className="text-4xl sm:text-5xl font-black text-white drop-shadow-2xl flex items-center justify-center gap-2">
            <span className="animate-bounce">🏝️</span>
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-cyan-100">
              Mojiland
            </span>
          </h1>
          <p className="text-cyan-100 font-bold text-sm mt-1 drop-shadow">
            {t('mojiland.subtitle')}
          </p>
        </div>

        {/* Progreso brutal */}
        <div
          className="mb-5 rounded-3xl p-5 relative overflow-hidden border-2 border-white/40 shadow-2xl"
          style={{
            background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.9) 0%, rgba(14, 165, 233, 0.95) 50%, rgba(2, 132, 199, 0.9) 100%)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <span className="text-4xl">🤿</span>
              <div>
                <span className="text-white font-black text-lg block">{t('mojiland.openWaterDiver')}</span>
                <span className="text-cyan-100 text-sm font-bold">
                  {t('mojiland.objectives', { completed: completedObjectives, total: totalObjectives })}
                </span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-3xl font-black text-white">{Math.round(courseProgress * 100)}%</span>
              <p className="text-cyan-100 text-xs font-bold">{t('mojiland.completed')}</p>
            </div>
          </div>
          <div className="h-3 rounded-full bg-black/20 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-cyan-200 via-emerald-400 to-green-400 transition-all duration-700 shadow-lg"
              style={{ width: `${Math.max(2, courseProgress * 100)}%` }}
            />
          </div>
        </div>

        {/* Mapa - Islas del océano */}
        <div
          className="relative h-72 sm:h-80 rounded-3xl mb-5 overflow-hidden border-4 border-amber-400/60 shadow-2xl"
          style={{
            background: 'linear-gradient(180deg, #06b6d4 0%, #0891b2 20%, #0e7490 50%, #155e75 80%, #0c4a6e 100%)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4), inset 0 2px 10px rgba(255, 255, 255, 0.1)',
          }}
        >
          {/* Camino completo - siempre visible, verde cuando completado (tipo progress bar) */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
            {/* Fondo del camino - línea completa en gris claro */}
            <path
              d="M 20 15 L 50 25 L 80 40 L 60 65 L 30 75"
              fill="none"
              stroke="rgba(255,255,255,0.4)"
              strokeWidth="6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* Progreso - verde segmento a segmento */}
            {(() => {
              const modules = openWaterCourse.modules
              const points: [number, number][] = modules.map(m => [m.position.x, m.position.y])
              let path = ''
              for (let i = 0; i < points.length - 1; i++) {
                const modProg = progress.modules.find(p => p.moduleId === modules[i].id)
                const completed = modProg && modProg.objectivesCompleted.filter(o => o.completed).length >= modules[i].objectives.length
                if (completed) {
                  if (path === '') path = `M ${points[i][0]} ${points[i][1]}`
                  path += ` L ${points[i + 1][0]} ${points[i + 1][1]}`
                } else break
              }
              return path ? (
                <path
                  d={path}
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="transition-all duration-500"
                />
              ) : null
            })()}
          </svg>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 px-4 py-2 rounded-full bg-black/40 backdrop-blur-md border border-white/20">
            <span className="text-xl">🗺️</span>
            <span className="font-black text-white text-sm">{t('mojiland.oceanOfKnowledge')}</span>
          </div>

          {openWaterCourse.modules.map((module, idx) => {
            const modProg = progress.modules.find(m => m.moduleId === module.id)
            const completed = modProg?.objectivesCompleted.filter(o => o.completed).length ?? 0
            const total = module.objectives.length
            const unlocked = modProg?.unlocked ?? false

            return (
              <TerritoryNode
                key={module.id}
                module={module}
                progress={{ completed, total, unlocked }}
                isSelected={selectedModule?.id === module.id}
                onSelect={() => setSelectedModule(unlocked ? module : null)}
                index={idx}
              />
            )
          })}

          <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/20 to-transparent" />
        </div>

        {/* Panel de objetivos - siempre visible si hay módulo */}
        {selectedModule ? (
          <div
            className="rounded-3xl p-6 mb-6 relative overflow-hidden border-2 border-white/30 shadow-2xl animate-correct-pop"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(207, 250, 254, 0.9) 100%)',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <span className="text-5xl drop-shadow">{selectedModule.emoji}</span>
                <div>
                  <h2 className="font-black text-gray-900 text-xl">{selectedModule.title}</h2>
                  <p className="text-sm text-gray-600 font-medium">{selectedModule.description}</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelectedModule(null)} className="w-10 h-10 p-0 min-w-[40px]">
                ✕
              </Button>
            </div>

            <p className="text-sm text-gray-600 mb-4 font-medium">
              {t('mojiland.completeInOrder')}
            </p>
            <div className="space-y-3">
              {selectedModule.objectives.map((obj, idx) => (
                <ObjectiveItem
                  key={obj.id}
                  objective={obj}
                  completed={mojilandStore.isObjectiveCompleted('open-water', obj.id)}
                  step={idx + 1}
                  isRecommended={!mojilandStore.isObjectiveCompleted('open-water', obj.id) &&
                    selectedModule.objectives.slice(0, idx).every(o => mojilandStore.isObjectiveCompleted('open-water', o.id))}
                />
              ))}
            </div>
          </div>
        ) : (
          <div
            className="rounded-3xl p-6 mb-6 text-center border-2 border-white/40 shadow-xl"
            style={{
              background: 'rgba(255, 255, 255, 0.25)',
              backdropFilter: 'blur(16px)',
            }}
          >
            <p className="text-white font-bold text-lg drop-shadow">
              {t('mojiland.touchIsland')}
            </p>
            <p className="text-cyan-100 text-sm mt-2 font-medium">
              {t('mojiland.completeQuizzes')}
            </p>
          </div>
        )}

        {/* Navegación */}
        <div className="flex flex-wrap justify-center gap-3">
          <div className="flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-white/90 backdrop-blur border-2 border-amber-200 shadow-lg">
            <span className="text-2xl">⭐</span>
            <span className="font-black text-gray-800">{t('common.level')} {gamificationStore.level}</span>
          </div>
          <Link
            to="/diving-academy"
            className="flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-bold border-2 border-indigo-300 hover:scale-105 transition-all shadow-lg hover:shadow-xl"
          >
            <span className="text-2xl">🤿</span>
            <span>{t('divingAcademy.title')}</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-white/90 backdrop-blur border-2 border-cyan-200 hover:bg-white hover:scale-105 transition-all shadow-lg"
          >
            <span className="text-2xl">🎮</span>
            <span className="font-bold text-gray-800">{t('nav.games')}</span>
          </Link>
        </div>
      </div>
    </div>
  )
})

export default Mojiland
